import { useState, useMemo, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Search, Instagram, Facebook, Youtube, Clock, Trash2, Pencil, CheckCircle, AlertTriangle, Timer, Send, ChevronLeft, ChevronRight } from 'lucide-react'
import { useDeletePost } from '@/api/hooks/usePoster'
import { toast } from 'sonner'
import type { ScheduledPost, Account } from '@/types'
import type { YouTubeChannel } from '@/api/hooks/useYouTube'

interface ListViewProps {
  posts: ScheduledPost[]
  accounts: Account[]
  ytChannels: YouTubeChannel[]
  onEdit?: (post: ScheduledPost) => void
}

function StatusBadge({ post }: { post: ScheduledPost }) {
  const status = post.status || (post.published ? 'published' : 'pending')
  if (status === 'published') {
    return (
      <Badge className="gap-1 text-[10px] rounded-md bg-primary/15 text-primary border-none font-semibold">
        <CheckCircle className="h-2.5 w-2.5" /> Publicado
      </Badge>
    )
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive" className="gap-1 text-[10px] rounded-md font-semibold">
        <AlertTriangle className="h-2.5 w-2.5" /> Falhou
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="gap-1 border-yellow-500/25 bg-yellow-500/10 text-[10px] text-yellow-600 dark:text-yellow-400 rounded-md font-semibold">
      <Timer className="h-2.5 w-2.5" /> Agendado
    </Badge>
  )
}

export function ListView({ posts, accounts, ytChannels, onEdit }: ListViewProps) {
  const deletePost = useDeletePost()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [platformFilter, setPlatformFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [accountFilter, setAccountFilter] = useState('all')
  const [postTypeFilter, setPostTypeFilter] = useState('all')
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 10

  // Reset page when any filter changes
  useEffect(() => { setPage(0) }, [search, statusFilter, platformFilter, categoryFilter, accountFilter, postTypeFilter, mediaTypeFilter, tagFilter])

  const categories = useMemo(() => {
    const cats = new Set<string>()
    posts.forEach(p => { if (p.category) cats.add(p.category) })
    return Array.from(cats).sort()
  }, [posts])

  const accountOptions = useMemo(() => {
    const map = new Map<string, string>()
    posts.forEach(p => {
      if (p.pageId && p.accountName) map.set(p.pageId, p.accountName)
    })
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [posts])

  const allTags = useMemo(() => {
    const tags = new Set<string>()
    accounts.forEach(a => { if (a.tags) a.tags.forEach(t => tags.add(t)) })
    ytChannels.forEach(ch => { if (ch.tags) ch.tags.forEach(t => tags.add(t)) })
    return Array.from(tags).sort()
  }, [accounts, ytChannels])

  const tagPageIds = useMemo(() => {
    if (tagFilter === 'all') return null
    const ids = new Set<string>()
    accounts.forEach(a => {
      if (a.tags?.includes(tagFilter) && a.pageId) ids.add(String(a.pageId))
    })
    return ids
  }, [accounts, tagFilter])

  const tagYtChannelIds = useMemo(() => {
    if (tagFilter === 'all') return null
    const ids = new Set<string>()
    ytChannels.forEach(ch => {
      if (ch.tags?.includes(tagFilter)) ids.add(ch.id)
    })
    return ids
  }, [ytChannels, tagFilter])

  const filtered = useMemo(() => {
    return posts.filter(p => {
      if (search) {
        const q = search.toLowerCase()
        if (!p.message?.toLowerCase().includes(q) && !p.accountName?.toLowerCase().includes(q) && !p.title?.toLowerCase().includes(q)) return false
      }
      if (statusFilter !== 'all') {
        const s = p.status || (p.published ? 'published' : 'pending')
        if (s !== statusFilter) return false
      }
      if (platformFilter !== 'all' && p.platform !== platformFilter) return false
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false
      if (accountFilter !== 'all' && p.pageId !== accountFilter) return false
      if (postTypeFilter !== 'all' && (p.postType || 'feed') !== postTypeFilter) return false
      if (mediaTypeFilter !== 'all' && (p.mediaType || 'image') !== mediaTypeFilter) return false
      if (tagPageIds || tagYtChannelIds) {
        const matchesPage = tagPageIds && tagPageIds.has(p.pageId)
        const matchesYt = tagYtChannelIds && p.youtubeChannelId && tagYtChannelIds.has(p.youtubeChannelId)
        if (!matchesPage && !matchesYt) return false
      }
      return true
    })
  }, [posts, search, statusFilter, platformFilter, categoryFilter, accountFilter, postTypeFilter, mediaTypeFilter, tagPageIds, tagYtChannelIds])

  const handleDelete = (postId: string) => {
    if (!confirm('Excluir post?')) return
    deletePost.mutate(postId, {
      onSuccess: () => toast.success('Post excluido!'),
    })
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2.5 rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-3.5 shadow-sm">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por mensagem ou conta..."
            className="h-8 pl-8 text-xs rounded-lg bg-muted/30 border-transparent focus:border-primary/30 focus:bg-transparent transition-colors"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-32 text-xs rounded-lg border-border/40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Agendados</SelectItem>
            <SelectItem value="published">Publicados</SelectItem>
            <SelectItem value="failed">Com falha</SelectItem>
          </SelectContent>
        </Select>
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="h-8 w-32 text-xs rounded-lg border-border/40"><SelectValue placeholder="Plataforma" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="ig">Instagram</SelectItem>
            <SelectItem value="fb">Facebook</SelectItem>
            <SelectItem value="yt">YouTube</SelectItem>
          </SelectContent>
        </Select>
        <Select value={postTypeFilter} onValueChange={setPostTypeFilter}>
          <SelectTrigger className="h-8 w-28 text-xs rounded-lg border-border/40"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="feed">Feed</SelectItem>
            <SelectItem value="story">Story</SelectItem>
          </SelectContent>
        </Select>
        <Select value={mediaTypeFilter} onValueChange={setMediaTypeFilter}>
          <SelectTrigger className="h-8 w-28 text-xs rounded-lg border-border/40"><SelectValue placeholder="Midia" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="image">Imagem</SelectItem>
            <SelectItem value="video">Video</SelectItem>
          </SelectContent>
        </Select>
        {accountOptions.length > 1 && (
          <Select value={accountFilter} onValueChange={setAccountFilter}>
            <SelectTrigger className="h-8 w-40 text-xs rounded-lg border-border/40"><SelectValue placeholder="Pagina" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas paginas</SelectItem>
              {accountOptions.map(([pageId, name]) => (
                <SelectItem key={pageId} value={pageId}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-8 w-32 text-xs rounded-lg border-border/40"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {allTags.length > 0 && (
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="h-8 w-28 text-xs rounded-lg border-border/40"><SelectValue placeholder="Tag" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tags</SelectItem>
              {allTags.map(tag => (
                <SelectItem key={tag} value={tag}>{tag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Badge variant="outline" className="text-[11px] rounded-lg border-border/40 font-medium tabular-nums">
          {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-border/40 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent bg-muted/20">
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Plataforma</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Tipo</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Conta</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Titulo</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Mensagem</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Categoria</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Data/Hora</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground text-right">Acoes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-16 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/30 mx-auto mb-3">
                    <Send className="h-6 w-6 text-muted-foreground/40" />
                  </div>
                  <p className="text-xs text-muted-foreground">Nenhum post encontrado</p>
                </TableCell>
              </TableRow>
            ) : (
              filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(post => {
                const canEdit = !post.published
                const isStory = post.postType === 'story'
                const isVideo = post.mediaType === 'video'
                return (
                  <TableRow key={post.id} className="transition-colors hover:bg-muted/20">
                    <TableCell>
                      <Badge variant="outline" className="gap-1 text-[10px] rounded-md border-border/40">
                        {post.platform === 'yt' ? (
                          <Youtube className="h-2.5 w-2.5 text-red-500" />
                        ) : post.platform === 'ig' ? (
                          <Instagram className="h-2.5 w-2.5 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <Facebook className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                        )}
                        {post.platform === 'yt' ? 'YT' : post.platform === 'ig' ? 'IG' : 'FB'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {isStory ? (
                          <Badge variant="outline" className="border-orange-500/25 bg-orange-500/10 text-[10px] text-orange-600 dark:text-orange-400 rounded-md">Story</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] rounded-md border-border/40">Feed</Badge>
                        )}
                        {post.campaignId && (
                          <Badge variant="outline" className="border-emerald-500/25 bg-emerald-500/10 text-[10px] text-emerald-600 dark:text-emerald-400 rounded-md">Camp.</Badge>
                        )}
                        {isVideo && (
                          <Badge variant="outline" className="text-[10px] rounded-md border-border/40">Video</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {post.accountName ? (() => {
                        const profileUrl = post.platform === 'yt' ? null
                          : post.platform === 'ig' && post.accountName.startsWith('@')
                            ? `https://instagram.com/${post.accountName.slice(1)}`
                            : post.platform === 'fb' && post.pageId
                              ? `https://facebook.com/${post.pageId}`
                              : null
                        return profileUrl ? (
                          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline transition-colors">{post.accountName}</a>
                        ) : post.accountName
                      })() : '-'}
                    </TableCell>
                    <TableCell>
                      {post.title ? (
                        <span className="max-w-[120px] truncate text-xs font-medium text-foreground block">{post.title}</span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="max-w-[200px] truncate text-xs text-muted-foreground">{post.message}</p>
                    </TableCell>
                    <TableCell>
                      {post.category ? (
                        <Badge variant="outline" className="text-[10px] rounded-md border-border/40">{post.category}</Badge>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
                        <Clock className="h-2.5 w-2.5 text-muted-foreground" />
                        {new Date(post.scheduledAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge post={post} /></TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <div className="flex items-center justify-end gap-1">
                          {onEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground transition-colors" onClick={() => onEdit(post)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive transition-colors" onClick={() => handleDelete(post.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        {filtered.length > PAGE_SIZE && (() => {
          const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
          return (
            <div className="flex items-center justify-between border-t border-border/30 bg-muted/10 backdrop-blur-sm px-4 py-2.5">
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {filtered.length} resultado{filtered.length !== 1 ? 's' : ''} &middot; Pagina {page + 1} de {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}
